import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import DevicesIcon from '@mui/icons-material/Devices';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface ApplianceType {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface UnitAppliance {
  id: string;
  quantity: number;
  typeId: string;
  unitId: string;
  type: ApplianceType | null;
  active: boolean;
}

interface UnitApplianceDialogProps {
  open: boolean;
  onClose: () => void;
  unitId: string;
  unitNumber: string;
}

const UnitApplianceDialog: React.FC<UnitApplianceDialogProps> = ({
  open,
  onClose,
  unitId,
  unitNumber
}) => {
  const [loading, setLoading] = useState(false);
  const [applianceTypes, setApplianceTypes] = useState<ApplianceType[]>([]);
  const [unitAppliances, setUnitAppliances] = useState<UnitAppliance[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newAppliance, setNewAppliance] = useState({
    typeId: '',
    quantity: 1
  });
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (open) {
      fetchApplianceTypes();
      fetchUnitAppliances();
    }
  }, [open, unitId]);

  const fetchApplianceTypes = async () => {
    try {
      const response = await DataService.get('api/appliance/all');

      if (response.ok) {
        const responseData = await response.json();
        let typesList: ApplianceType[] = [];

        if (responseData.success && responseData.data) {
          typesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          typesList = responseData;
        }

        setApplianceTypes(typesList.filter(type => type.active));
      }
    } catch (error) {
      console.error('Error fetching appliance types:', error);
    }
  };

  const fetchUnitAppliances = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await DataService.get(`api/UnitAppliance/unit-list/${unitId}`);

      if (response.ok) {
        const responseData = await response.json();
        let appliancesList: UnitAppliance[] = [];

        if (responseData.success && responseData.data) {
          appliancesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          appliancesList = responseData;
        }

        setUnitAppliances(appliancesList);
      } else {
        throw new Error('Failed to fetch unit appliances');
      }
    } catch (error) {
      console.error('Error fetching unit appliances:', error);
      setError('Failed to load appliances. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAppliance = async () => {
    if (!newAppliance.typeId || newAppliance.quantity < 1) {
      dispatch(showAlert({
        message: 'Please select an appliance type and enter a valid quantity.',
        type: 'error'
      }));
      return;
    }

    try {
      const payload = {
        quantity: newAppliance.quantity,
        typeId: newAppliance.typeId,
        unitId: unitId
      };

      const response = await DataService.post('api/UnitAppliance/create', payload);

      if (response.ok) {
        dispatch(showAlert({
          message: 'Appliance added successfully!',
          type: 'success'
        }));

        setNewAppliance({ typeId: '', quantity: 1 });
        fetchUnitAppliances();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add appliance');
      }
    } catch (error) {
      console.error('Error adding appliance:', error);
      dispatch(showAlert({
        message: error instanceof Error ? error.message : 'Failed to add appliance. Please try again.',
        type: 'error'
      }));
    }
  };

  const handleDeleteAppliance = async (applianceId: string) => {
    try {
      const response = await DataService.delete(`api/UnitAppliance/delete/${applianceId}`, {});

      if (response.ok) {
        dispatch(showAlert({
          message: 'Appliance removed successfully!',
          type: 'success'
        }));
        fetchUnitAppliances();
      } else {
        throw new Error('Failed to delete appliance');
      }
    } catch (error) {
      console.error('Error deleting appliance:', error);
      dispatch(showAlert({
        message: 'Failed to remove appliance. Please try again.',
        type: 'error'
      }));
    }
  };

  const getApplianceName = (typeId: string): string => {
    const appliance = applianceTypes.find(type => type.id === typeId);
    return appliance ? appliance.name : typeId;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DevicesIcon />
          Manage Appliances - Unit {unitNumber}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        {/* Add New Appliance Section */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Add New Appliance
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                value={newAppliance.typeId}
                onChange={(e) => setNewAppliance({ ...newAppliance, typeId: e.target.value })}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Appliance Type
                </MenuItem>
                {applianceTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="number"
              label="Quantity"
              value={newAppliance.quantity}
              onChange={(e) => setNewAppliance({ ...newAppliance, quantity: parseInt(e.target.value) || 1 })}
              inputProps={{ min: 1 }}
              sx={{ width: 100 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddAppliance}
              size="small"
            >
              Add
            </Button>
          </Box>
        </Box>

        {/* Appliances List */}
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          Current Appliances ({unitAppliances.length})
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
            <CircularProgress />
          </Box>
        ) : unitAppliances.length === 0 ? (
          <Alert severity="info">
            No appliances added yet. Use the form above to add appliances to this unit.
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.75, px: 1 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Appliance Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 0.5, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unitAppliances.map((appliance, index) => (
                  <TableRow key={appliance.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DevicesIcon fontSize="small" color="primary" />
                        <Typography variant="body2">
                          {getApplianceName(appliance.typeId)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{appliance.quantity}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteAppliance(appliance.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnitApplianceDialog;
